import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, MapPin } from "lucide-react";
import { apiRequest } from "@/api/client";
import { getAudit, listAudits, pendingCount, putAudit } from "@/lib/auditStore";
import { useSync } from "./SyncContext";

export const STATUS_LABEL = {
  assegnato: ["Da iniziare", "bg-sky-100 text-sky-900"], in_compilazione: ["In compilazione", "bg-amber-100 text-amber-900"],
  da_integrare: ["Da integrare", "bg-rose-100 text-rose-900"], inviato: ["Inviato", "bg-slate-200 text-slate-800"],
  in_elaborazione: ["In elaborazione", "bg-slate-200 text-slate-800"], bozza_pronta: ["Inviato", "bg-slate-200 text-slate-800"],
  in_revisione: ["In revisione", "bg-slate-200 text-slate-800"], approvato: ["Chiuso", "bg-emerald-100 text-emerald-900"],
  consegnato: ["Chiuso", "bg-emerald-100 text-emerald-900"],
};

export default function AuditorHome() {
  const [rows, setRows] = useState([]);
  const [fromCache, setFromCache] = useState(false);
  const { online, subscribe } = useSync();

  const load = useCallback(async () => {
    try {
      const list = await apiRequest("/auditor/audits");
      setRows(list); setFromCache(false);
      // scarica subito la checklist degli audit ancora da compilare: servirà senza rete
      for (const a of list.filter((x) => !x.readonly)) {
        if ((await pendingCount(a.id)) > 0) continue;
        try {
          const d = await apiRequest(`/auditor/audits/${a.id}`);
          const old = await getAudit(a.id);
          await putAudit({ ...d, id: a.id, draftEvidence: old?.draftEvidence || {}, signatures: old?.signatures || {}, savedAt: new Date().toISOString() });
        } catch { /* proseguo con gli altri */ }
      }
    } catch {
      const cached = await listAudits().catch(() => []);
      setRows(cached.map((c) => c.audit)); setFromCache(true);
    }
  }, []);

  useEffect(() => { load(); }, [load, online]);
  useEffect(() => subscribe(() => load()), [subscribe, load]);

  return (
    <div className="space-y-3" data-testid="auditor-home">
      <h1 className="font-heading text-2xl font-bold">I tuoi audit</h1>
      {fromCache && <p className="rounded bg-amber-100 p-3 text-sm">Sei offline: vedi gli audit già scaricati sul dispositivo.</p>}
      {rows.length === 0 && <p className="rounded-lg border bg-white p-6 text-center text-slate-600">Nessun audit assegnato.</p>}
      {rows.map((a) => {
        const [label, cls] = STATUS_LABEL[a.status] || [a.status, "bg-slate-100"];
        return (
          <Link key={a.id} to={`/auditor/audit/${a.id}`} className="flex items-center gap-3 rounded-xl border bg-white p-4 shadow-sm active:bg-slate-50" data-testid="audit-card">
            <div className="min-w-0 flex-1 space-y-1">
              <p className="truncate text-lg font-bold">{a.company_name}</p>
              <p className="flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-4 w-4" />{a.site_label} · {a.operating_address}</p>
              <p className="flex items-center gap-1 text-sm text-slate-600"><CalendarDays className="h-4 w-4" />{a.scheduled_date}</p>
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>{label}</span>
              {a.integration_request && a.status === "da_integrare" && <p className="text-sm font-medium text-rose-800">Richiesta: {a.integration_request.reason}</p>}
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </Link>
        );
      })}
    </div>
  );
}
